import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import type { Order } from '@/utils/supabase';

// ──────────────────────────────────────────────────────────────────────────────
// useResilientOrder — "Zero-Ping Resilience" for E-Commerce Checkout
// ──────────────────────────────────────────────────────────────────────────────
//
// ARCHITECTURE: Strict Separation of Concerns
//
//   This hook is ONLY responsible for the Supabase database call.
//   It does NOT touch the Zustand store, Ably events, or staff assignment.
//   Those side-effects are handled by the caller (Checkout.tsx) via
//   orderStore.addCreatedOrder() AFTER this hook returns success.
//
//   This prevents the double-RPC bug where both the hook and the store
//   independently call supabase.rpc('place_order_transaction').
//
// RESILIENCE STRATEGIES:
//
//   1. Silent Auto-Retry    — catches stale-socket errors and retries once
//   2. AbortController      — strict 8s timeout to short-circuit hung requests
//   3. JIT Connection Warm  — pre-warms TCP/TLS on hover/touch before click
//   4. Foolproof UI State   — finally block always resets isLoading
//   5. Unmount Cleanup      — useEffect abort on component unmount
//
// ──────────────────────────────────────────────────────────────────────────────

/** Timeout in ms for each attempt (initial + retry). */
const REQUEST_TIMEOUT_MS = 8_000;

/** Max retry count after network-level failures. */
const MAX_RETRIES = 1;

// ─── Type Definitions ────────────────────────────────────────────────────────

export interface CreateOrderData {
  id: string;
  user_id: string | null;
  subtotal: number;
  delivery_fee: number;
  discount: number;
  voucher_id: string | null;
  total: number;
  payment_method: Order['payment_method'];
  payment_status: Order['payment_status'];
  status: Order['status'];
  delivery_address: Order['delivery_address'];
  contact_phone: string;
  notes?: string | null;
  order_items: Array<{
    product_id: string;
    quantity: number;
    price_at_time: number;
    total: number;
  }>;
  guest_email?: string | null;
  guest_name?: string | null;
  guest_phone?: string | null;
}

/** The shape returned by supabase.rpc('place_order_transaction') on success. */
export interface RpcOrderResult {
  order_id: string;
}

export interface ResilientOrderResult {
  /** True while the order request (including any retry) is in flight. */
  isLoading: boolean;
  /** The error message from the last failed attempt, or null. */
  error: string | null;
  /**
   * Submit the order with full dead-socket resilience.
   * Returns the server-generated order_id on success, or null on failure.
   * IMPORTANT: This ONLY calls the database. It does NOT update Zustand state.
   */
  submitOrder: (orderData: CreateOrderData) => Promise<string | null>;
  /** Warm the Supabase TCP/TLS connection. Bind to onMouseEnter / onTouchStart. */
  warmConnection: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Determines whether an error is a network-level failure that indicates a
 * stale/dead socket — the kind that a single retry will fix after the
 * browser's network stack wakes up.
 *
 * Intentionally narrow: only transient fetch failures and AbortController
 * timeouts qualify. Application-level errors (4xx, RLS violations,
 * constraint errors) are NOT retried.
 */
function isNetworkError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') {
    // AbortController fired because the request hung past REQUEST_TIMEOUT_MS.
    return true;
  }
  if (err instanceof TypeError) {
    // Browser throws TypeError("Failed to fetch") when the socket is dead,
    // CORS is broken, or DNS fails. All are transient after tab resume.
    const msg = err.message.toLowerCase();
    return msg.includes('failed to fetch') || msg.includes('network');
  }
  // Supabase client sometimes wraps fetch errors in a plain Error.
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return (
      msg.includes('failed to fetch') ||
      msg.includes('networkerror') ||
      msg.includes('load failed') || // Safari-specific
      msg.includes('the operation was aborted')
    );
  }
  return false;
}

/**
 * Creates a Supabase RPC call with an AbortController that auto-aborts
 * after `timeoutMs`.
 *
 * Why not Promise.race with setTimeout?
 * -> race() leaves the losing promise dangling (still consuming the socket).
 *    AbortController actually cancels the underlying fetch, freeing the
 *    connection immediately for the retry.
 */
async function createTimedRpc(
  orderData: CreateOrderData,
  timeoutMs: number,
  signal: AbortSignal,
): Promise<{ data: { order_id?: string } | null; error: { message?: string; details?: string; hint?: string } | null }> {
  // Wire the abort timeout
  const timeoutId = setTimeout(() => {
    // We can't call abort on the parent controller from here because
    // signal is read-only. Instead, we create a local mechanism:
    // the parent controller.abort() is called from the retry loop.
  }, timeoutMs);

  try {
    const result = await supabase
      .rpc('place_order_transaction', {
        p_user_id: orderData.user_id,
        p_subtotal: orderData.subtotal,
        p_delivery_fee: orderData.delivery_fee,
        p_discount: orderData.discount,
        p_voucher_id: orderData.voucher_id,
        p_total: orderData.total,
        p_payment_method: orderData.payment_method,
        p_payment_status: orderData.payment_status,
        p_status: orderData.status,
        p_delivery_address: orderData.delivery_address,
        p_contact_phone: orderData.contact_phone,
        p_notes: orderData.notes || null,
        p_order_items: orderData.order_items,
        p_guest_email: orderData.guest_email || null,
        p_guest_name: orderData.guest_name || null,
        p_guest_phone: orderData.guest_phone || null,
      })
      .abortSignal(signal);
    
    clearTimeout(timeoutId);
    return result;
  } catch (err: unknown) {
    clearTimeout(timeoutId);
    throw err;
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useResilientOrder(): ResilientOrderResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to track the active AbortController so we can cancel on unmount
  // or if the user somehow triggers a second submission.
  const activeController = useRef<AbortController | null>(null);

  // ── Strategy 5: Unmount Cleanup ──────────────────────────────────────────
  // Abort any in-flight request if the component unmounts (e.g., user
  // navigates away mid-request). This prevents state updates on unmounted
  // components and frees the network socket.
  useEffect(() => {
    return () => {
      if (activeController.current) {
        activeController.current.abort();
        activeController.current = null;
      }
    };
  }, []);

  // ── Strategy 3: JIT Connection Warming ───────────────────────────────────
  //
  // Fires a near-zero-cost request to Supabase when the user hovers over or
  // touches the "Place Order" button. This pre-establishes the TCP/TLS
  // handshake so the actual .rpc() call is lightning-fast.
  //
  // Why supabase.auth.getSession()?
  // -> It reads from memory/localStorage first, but the SDK may refresh
  //    the token over the network if it's near expiry. Either way, it
  //    warms the connection and ensures a valid JWT is ready.
  //
  // Why not poll?
  // -> This only fires on intentional user interaction (hover/touch),
  //    typically 100-300ms before the click. Zero background cost.
  //
  const isWarmingRef = useRef(false);

  const warmConnection = useCallback(() => {
    // Debounce: don't fire multiple warmers for rapid hover/touch events
    if (isWarmingRef.current) return;
    isWarmingRef.current = true;

    // Fire-and-forget: we don't care about the result, only about waking
    // the socket and refreshing the auth token.
    Promise.all([
      // 1. Refresh the auth token (local-first, network if expiring)
      supabase.auth.getSession(),
      // 2. Warm the PostgREST connection pool with a tiny query
      supabase.from('products').select('id').limit(1),
    ])
      .catch(() => {
        // Swallow errors: this is best-effort. The retry logic in
        // submitOrder handles actual failures.
        if (import.meta.env.DEV) {
          console.debug('[useResilientOrder] JIT warm failed (non-critical)');
        }
      })
      .finally(() => {
        isWarmingRef.current = false;
      });
  }, []);

  // ── Core: Resilient Order Submission ─────────────────────────────────────

  const submitOrder = useCallback(
    async (orderData: CreateOrderData): Promise<string | null> => {
      // Prevent double submission
      if (isLoading) return null;

      setIsLoading(true);
      setError(null);

      // Cancel any lingering previous request
      if (activeController.current) {
        activeController.current.abort();
        activeController.current = null;
      }

      let lastError: unknown = null;

      try {
        // ── Strategy 1 + 2: Timed RPC with Silent Auto-Retry ─────────────
        //
        // Attempt 0: Fire the RPC with an 8s AbortController timeout.
        //   -> If the socket is alive -> succeeds in ~200-500ms
        //   -> If the socket is dead  -> hangs until abort at 8s, then catch
        //
        // Attempt 1 (retry): The first failure "wakes" the browser's network
        //   stack. The immediate retry gets a fresh socket and succeeds
        //   instantly -- the user never notices the first failure.
        //
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            // Create a fresh AbortController per attempt
            const controller = new AbortController();
            activeController.current = controller;

            // Set up the aggressive timeout
            const timeoutId = setTimeout(
              () => controller.abort(),
              REQUEST_TIMEOUT_MS,
            );

            try {
              const { data, error: rpcError } = await createTimedRpc(
                orderData,
                REQUEST_TIMEOUT_MS,
                controller.signal,
              );

              clearTimeout(timeoutId);

              // ── Application-level errors (NOT retried) ─────────────────
              if (rpcError) {
                const fullMsg = [rpcError.message, rpcError.details, rpcError.hint]
                  .filter(Boolean)
                  .join(' | ');
                throw new Error(fullMsg || 'Unknown backend error');
              }

              // ── Success! ───────────────────────────────────────────────
              // Return ONLY the server-generated order ID.
              // The caller (Checkout.tsx) is responsible for passing this
              // to orderStore.addCreatedOrder() for UI state + side-effects.
              const serverOrderId: string = data?.order_id || orderData.id;

              if (import.meta.env.DEV) {
                console.log(
                  `[useResilientOrder] Order placed on attempt ${attempt + 1}:`,
                  serverOrderId,
                );
              }

              return serverOrderId;
            } catch (innerErr) {
              clearTimeout(timeoutId);
              throw innerErr;
            }
          } catch (err) {
            lastError = err;

            // Only retry on network-level errors (dead socket, timeout)
            if (isNetworkError(err) && attempt < MAX_RETRIES) {
              if (import.meta.env.DEV) {
                console.warn(
                  `[useResilientOrder] Attempt ${attempt + 1} failed (network). ` +
                    `Retrying immediately to wake network stack...`,
                  err,
                );
              }
              // Don't await or delay: retry IMMEDIATELY. The first failure
              // already woke the network stack; the retry gets a fresh socket.
              continue;
            }

            // Non-network error or retries exhausted -- break out
            break;
          }
        }

        // ── All attempts exhausted ─────────────────────────────────────────
        // Format the error for the UI. We preserve the pipe-delimited format
        // (THROTTLING|, TIMEOUT|, STOCK_ERROR|) so Checkout.tsx can handle
        // them with its existing toast logic.
        if (lastError instanceof Error) {
          if (isNetworkError(lastError)) {
            const msg =
              'TIMEOUT|Order placement timed out. Please check your connection and try again.';
            setError(msg);
            throw new Error(msg);
          }
          setError(lastError.message);
          throw lastError;
        }

        const fallbackMsg = 'Order creation failed. Please try again.';
        setError(fallbackMsg);
        throw new Error(fallbackMsg);
      } finally {
        // ── Strategy 4: Foolproof UI State ───────────────────────────────
        //
        // ALWAYS reset loading state, even if an unexpected error occurred.
        // This prevents the dreaded infinite spinner that kills conversions.
        setIsLoading(false);
        activeController.current = null;
      }
    },
    [isLoading],
  );

  return {
    isLoading,
    error,
    submitOrder,
    warmConnection,
  };
}
